import React, {
  FC,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  formatDateTime,
} from 'utils/date';
import isNil from 'lodash/isNil';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import AmountTag from 'pages/Dashboard/pages/Billing/AmountTag';
import ToolTip from 'core/components/Tooltip';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Link,
} from 'react-router-dom';
import LinkIcon from 'core/icons/link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from 'core/icons/chevronRight';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import {
  LedgerRow,
} from 'pages/Dashboard/pages/Ledger/types';
import {
  getToggleColumnTitle,
} from 'pages/Dashboard/pages/Billing/Charges/columns';
import ServiceCell from 'pages/Dashboard/pages/Ledger/ServiceCell';
import InsuranceBalanceExplanation from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation';
import InsuranceBalanceColumnTitle from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation/InsuranceBalanceColumnTitle';
import {
  isEmptyOrDash,
} from 'pages/Dashboard/pages/Ledger/helper';
import {
  useDefaultPatient,
} from 'views/EMR/stores/entities';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';

type TagProps = {
  tag: string;
  encounterDate?: string;
};

const commonClasses = '!border-0 !p-0 !text-blue-500 font-semibold flex items-center gap-1';

function EOBTag({ tag: eobNumber, encounterDate }: TagProps) {
  const defaultPatient = useDefaultPatient();
  return (
    <ToolTip title={(
      <div className="flex flex-col gap-2">
        <span className="text-gray-500">
          {eobNumber}
        </span>
        <span className="italic">Open EOB</span>
      </div>
    )}
    >
      <IconButton
        color="info"
        className={commonClasses}
        onClick={() => {
          paymentsEventChannel.emit('onEOBDialogOpen', {
            eobNumber: eobNumber ?? '',
            defaultPatient: defaultPatient as PatientCompact,
            defaultStartDate: encounterDate,
          });
        }}
      >
        <LinkIcon className="!w-3 !h-3 !text-blue-500" />
      </IconButton>
    </ToolTip>
  );
}

function ClaimTag({ tag: claimId }: TagProps) {
  return (
    <ToolTip title="Open Claim Form">
      <Link
        to={`/billing/claims/${claimId}`}
        target="_blank"
        className={commonClasses}
      >
        <LinkIcon className="!w-3 !h-3 !text-blue-500" />
      </Link>
    </ToolTip>
  );
}

const componentByTag: Record<string, FC<TagProps>> = {
  EOB: EOBTag,
  ERA: EOBTag,
  'Primary Claim': ClaimTag,
  'Secondary Claim': ClaimTag,
};

const defaultAmountCellSize = 100;

type ToggleRowCellProps = {
  row: Row<LedgerRow>;
};

function ToggleRowCell({ row }: ToggleRowCellProps) {
  const expandable = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const Icon = isExpanded && expandable ? ExpandMoreIcon : ChevronRightIcon;
  const iconTooltip = isExpanded ? 'Collapse' : 'Expand';

  return (!row.original.isTransaction && (
    <ToolTip title={!expandable ? 'No transaction logs' : iconTooltip}>
      <span>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          disabled={!expandable}
          className={clsx('!p-1', { '!cursor-not-allowed !pointer-events-auto opacity-30': !expandable })}
          disableRipple={!expandable}
        >
          <Icon className="!text-gray-500" />
        </IconButton>
      </span>
    </ToolTip>
  ));
}

const toggleRowColumn: ColumnDef<LedgerRow> = {
  id: 'toggleRow',
  size: 48,
  columnTitle: getToggleColumnTitle,
  cell: ({ row }) => <ToggleRowCell row={row} />,
};

const dateColumn: ColumnDef<LedgerRow> = {
  id: 'encounterDate',
  accessorKey: 'encounterDate',
  header: 'Date',
  size: 100,
  cell: ({ row: { original: {
    isTransaction,
    transactionDetails,
    encounterDate,
  } } }) => (isTransaction
    ? (
      <div>
        {isEmptyOrDash(transactionDetails)
          ? '--'
          : formatDateTime(transactionDetails ?? '', true)}
      </div>
    )
    : (
      <div className="flex items-center gap-2">
        <div className="w-12 font-semibold">
          {formatDateTime(encounterDate ?? '')}
        </div>
      </div>
    )),
};

const serviceColumn: ColumnDef<LedgerRow> = {
  id: 'service',
  accessorKey: 'service',
  header: 'Service',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <ServiceCell {...row.original} />
  ),
};

const netChargesColumn: ColumnDef<LedgerRow> = {
  id: 'netCharges',
  accessorKey: 'netCharges',
  header: 'Charges',
  size: defaultAmountCellSize,
  cell: ({ row: { original: { netCharges, isTransaction, chargeAmount } } }) => (
    <AmountTag
      value={(isTransaction ? chargeAmount : netCharges) ?? 0}
    />
  ),
};

const insurancePaidColumn: ColumnDef<LedgerRow> = {
  id: 'insurancePaid',
  accessorKey: 'insurancePaid',
  columnTitle: (
    <>
      Insurance
      <br />
      Paid
    </>
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { insurancePaid, isTransaction } } }) => (isTransaction
    ? '--'
    : (
      <AmountTag
        value={insurancePaid ?? 0}
      />
    )),
};

const insuranceBalanceColumn: ColumnDef<LedgerRow> = {
  id: 'insuranceBalance',
  accessorKey: 'insuranceBalance',
  columnTitle: (
    <InsuranceBalanceColumnTitle hintStorageKey="ledger-insurance-balance-hint" />
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { insuranceBalance, isTransaction, encounterId } } }) => (
    isTransaction
      ? '--'
      : (
        <div className="flex items-center gap-0.5">
          <InsuranceBalanceExplanation
            encounterId={encounterId ?? 0}
            amount={insuranceBalance ?? 0}
          />
        </div>
      )
  ),
};

const patientPaidColumn: ColumnDef<LedgerRow> = {
  id: 'patientPaid',
  accessorKey: 'patientPaid',
  columnTitle: (
    <>
      Patient
      <br />
      Paid
    </>
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { patientPaid, isTransaction } } }) => (isTransaction
    ? '--'
    : (
      <AmountTag
        value={patientPaid ?? 0}
      />
    )),
};

const patientBalanceColumn: ColumnDef<LedgerRow> = {
  id: 'patientBalance',
  accessorKey: 'patientBalance',
  columnTitle: (
    <>
      Patient
      <br />
      Balance
    </>
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { patientBalance, isTransaction } } }) => (isTransaction
    ? '--'
    : (
      <AmountTag
        value={patientBalance ?? 0}
      />
    )),
};

const patientDueColumn: ColumnDef<LedgerRow> = {
  id: 'patientDue',
  accessorKey: 'patientDue',
  columnTitle: (
    <>
      Appointment
      <br />
      Due
    </>
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { patientDue, isTransaction } } }) => (isTransaction
    ? '--'
    : (
      <AmountTag
        value={patientDue ?? 0}
      />
    )),
};

const allowedAmountColumn: ColumnDef<LedgerRow> = {
  id: 'allowedAmount',
  header: 'Allowed',
  size: defaultAmountCellSize,
  cell: ({ row: { original: { allowedAmount, isTransaction } } }) => (isTransaction
    ? (
      <AmountTag
        value={allowedAmount ?? 0}
      />
    )
    : '--'),
};

const adjustmentsAmountColumn: ColumnDef<LedgerRow> = {
  id: 'adjustmentsAmount',
  columnTitle: 'Adjustments',
  size: defaultAmountCellSize,
  cell: ({ row: { original: { adjustmentsAmount, isTransaction } } }) => (isTransaction
    ? (
      <AmountTag
        value={adjustmentsAmount ?? 0}
      />
    )
    : '--'),
};

const writeOffAmountColumn: ColumnDef<LedgerRow> = {
  id: 'writeOffAmount',
  columnTitle: 'Write Off',
  size: defaultAmountCellSize,
  cell: ({ row: { original: { writeOffAmount, isTransaction } } }) => (isTransaction
    ? (
      <AmountTag
        value={writeOffAmount ?? 0}
      />
    )
    : '--'),
};

const patientResponsibilityAmountColumn: ColumnDef<LedgerRow> = {
  id: 'patientResponsibilityAmount',
  columnTitle: (
    <>
      Patient
      <br />
      Responsibility
    </>
  ),
  size: defaultAmountCellSize,
  cell: ({ row: { original: { patientResponsibilityAmount, isTransaction } } }) => (isTransaction
    ? (
      <AmountTag
        value={patientResponsibilityAmount ?? 0}
      />
    )
    : '--'),
};

const paidAmountColumn: ColumnDef<LedgerRow> = {
  id: 'paidAmount',
  columnTitle: (
    <>
      Paid
      <br />
      Amount
    </>
  ),
  size: 92,
  cell: ({ row: { original: { paidAmount, isTransaction } } }) => (isTransaction
    ? (
      <AmountTag
        value={paidAmount ?? 0}
      />
    )
    : '--'),
};

const tagColumn: ColumnDef<LedgerRow> = {
  id: 'tag',
  size: 32,
  columnTitle: '',
  cell: ({ row: { original: { tag, category, encounterDate } } }) => {
    const Component = componentByTag[category ?? ''];

    return (
      <div className="flex justify-end w-full">
        {!isNil(Component) ? (
          <Component tag={tag ?? ''} encounterDate={encounterDate} />
        ) : '--'}
      </div>
    );
  },
};

export default [
  toggleRowColumn,
  dateColumn,
  serviceColumn,
  netChargesColumn,
  insurancePaidColumn,
  insuranceBalanceColumn,
  patientPaidColumn,
  patientBalanceColumn,
  patientDueColumn,
  allowedAmountColumn,
  adjustmentsAmountColumn,
  writeOffAmountColumn,
  patientResponsibilityAmountColumn,
  paidAmountColumn,
  tagColumn,
];
